import { ALL_SUBJECTS_LABEL, type ActivityKind, type GeneratedStudyBlock, type GeneratedWeek, type PlanConfiguration, type PlanSubject, type PlanWeekProjection, type PreviousSubjectPerformance } from '../types/plan';
import { addLocalDays, endOfSaturdayWeek, startOfSundayWeek } from '../utils/date';
import { availabilityMinutes, minutesToTime, timeToMinutes } from '../utils/time';

const activityLabels: Record<ActivityKind, string> = {
  review: 'Memorização/Revisão',
  theory: 'Estudo teórico',
  exercises: 'Exercícios',
  simulation: 'Simulado',
  writing: 'Redação ou Prova Dissertativa'
};

function adjustedSubjects(subjects: PlanSubject[], performance: PreviousSubjectPerformance[]) {
  const selected = subjects.filter((subject) => subject.selected);
  const scored = selected.map((subject) => {
    const previous = performance.find((item) => item.subjectName === subject.name);
    const accuracyBoost = previous ? Math.max(0, 70 - previous.accuracy) / 100 : 0;
    const completionBoost = previous ? Math.max(0, 80 - previous.completion) / 160 : 0;
    const pendingBoost = previous ? Math.min(0.15, previous.pendingEssential * 0.05) : 0;
    const absenceBoost = previous ? Math.min(0.1, previous.daysSinceLastStudy / 140) : 0;
    return { ...subject, adjusted: Math.max(0.1, subject.percent) * (1 + Math.min(0.35, accuracyBoost + completionBoost + pendingBoost + absenceBoost)) };
  });
  const total = scored.reduce((sum, subject) => sum + subject.adjusted, 0) || 1;
  return scored.map((subject) => ({ ...subject, adjustedPercent: (subject.adjusted / total) * 100 }));
}

function classifyPriority(percent: number) {
  if (percent >= 25) return 'Muito alta';
  if (percent >= 15) return 'Alta';
  if (percent >= 8) return 'Média';
  return 'Baixa';
}

export function generateStudyWeek(config: PlanConfiguration, performance: PreviousSubjectPerformance[] = [], variation = 0): GeneratedWeek {
  const weekNumber = Math.max(1, config.currentWeek || 1);
  const weekStart = addLocalDays(startOfSundayWeek(config.startDate), (weekNumber - 1) * 7);
  const weekEnd = endOfSaturdayWeek(weekStart);
  const generationKey = `${config.id}:week:${weekNumber}:${config.updatedAt}:suggestion:${variation}`;
  const warnings: GeneratedWeek['warnings'] = [];
  const selectedSubjects = adjustedSubjects(config.subjects, performance);
  const enabledActivities = config.activities.filter((activity) => activity.enabled);
  const availableMinutes = Math.min(availabilityMinutes(config.availability), config.manualWeeklyMinutes || Number.MAX_SAFE_INTEGER);
  const blocks: GeneratedStudyBlock[] = [];
  let plannedTotal = 0;
  let sequence = 0;

  if (!selectedSubjects.length) warnings.push({ code: 'subjects', message: 'Selecione ao menos uma disciplina para gerar a semana.' });
  if (!availableMinutes) warnings.push({ code: 'availability', message: 'Informe ao menos uma faixa de horário disponível.' });

  const slots = config.availability
    .filter((day) => day.enabled)
    .flatMap((day) => day.ranges.map((range) => ({
      weekday: day.weekday,
      date: addLocalDays(weekStart, day.weekday),
      cursor: timeToMinutes(range.start),
      end: timeToMinutes(range.end),
      breakMinutes: Math.max(0, range.breakMinutes || config.cycleBreakMinutes || 0),
      lastSubject: ''
    })))
    .sort((first, second) => first.weekday - second.weekday || first.cursor - second.cursor);

  const allocated = new Map(selectedSubjects.map((subject) => [subject.id, 0]));
  const target = new Map(selectedSubjects.map((subject) => [subject.id, availableMinutes * subject.adjustedPercent / 100]));
  const chooseSubject = (slotSubject: string) => [...selectedSubjects].sort((first, second) => {
    const firstNeed = (target.get(first.id) || 0) - (allocated.get(first.id) || 0) + (first.name === slotSubject ? -30 : 0);
    const secondNeed = (target.get(second.id) || 0) - (allocated.get(second.id) || 0) + (second.name === slotSubject ? -30 : 0);
    return secondNeed - firstNeed;
  })[0];

  const appendBlock = (activity: ActivityKind, duration: number, slot: typeof slots[number], subject: PlanSubject, questions = 0, crossSubject = false) => {
    const start = slot.cursor;
    const end = start + duration;
    blocks.push({
      id: `plan-${generationKey.replace(/[^a-z0-9]/gi, '-')}-${sequence++}`,
      planId: config.id,
      weekNumber,
      scheduledDate: slot.date,
      weekday: slot.weekday,
      startTime: minutesToTime(start),
      endTime: minutesToTime(end),
      durationMinutes: duration,
      activity,
      activityLabel: activityLabels[activity],
      subjectId: crossSubject ? undefined : subject.id,
      subjectName: crossSubject ? ALL_SUBJECTS_LABEL : activity === 'writing' ? (config.activities.find((item) => item.kind === 'writing')?.writingType || activityLabels.writing) : subject.name,
      priority: classifyPriority('adjustedPercent' in subject ? Number(subject.adjustedPercent) : subject.percent),
      estimatedQuestions: questions,
      status: 'Rascunho',
      source: performance.length ? 'recalculated' : 'generated',
      generationKey
    });
    if (!crossSubject) allocated.set(subject.id, (allocated.get(subject.id) || 0) + duration);
    plannedTotal += duration;
    slot.cursor = end;
    if (!crossSubject) slot.lastSubject = subject.name;
  };

  const placeBlock = (activity: ActivityKind, duration: number, preferredWeekday?: number, questions = 0, fixedSubject?: PlanSubject, strictPreferred = false, crossSubject = false) => {
    if (plannedTotal + duration > availableMinutes) return false;
    const orderedSlots = [...slots].sort((first, second) => {
      const firstPreference = preferredWeekday === undefined ? 0 : first.weekday === preferredWeekday ? -1000 : 0;
      const secondPreference = preferredWeekday === undefined ? 0 : second.weekday === preferredWeekday ? -1000 : 0;
      if (firstPreference !== secondPreference) return firstPreference - secondPreference;
      const firstPosition = (first.weekday * 1_440 + first.cursor + variation * 97) % 10_080;
      const secondPosition = (second.weekday * 1_440 + second.cursor + variation * 97) % 10_080;
      return firstPosition - secondPosition;
    });
    const candidateSlots = strictPreferred && preferredWeekday !== undefined ? orderedSlots.filter((candidate) => candidate.weekday === preferredWeekday) : orderedSlots;
    const slot = candidateSlots.find((candidate) => candidate.end - candidate.cursor >= duration);
    if (!slot) return false;
    const subject = fixedSubject || chooseSubject(slot.lastSubject);
    if (!subject) return false;
    appendBlock(activity, duration, slot, subject, questions, crossSubject);
    // O intervalo é reservado dentro da faixa disponível e nunca empurra um bloco para fora do horário informado.
    slot.cursor = Math.min(slot.end, slot.cursor + slot.breakMinutes);
    return true;
  };

  const placeTheoryWithExercises = (theory: typeof enabledActivities[number], exercises: typeof enabledActivities[number], subject: PlanSubject) => {
    const requiredMinutes = theory.durationMinutes + exercises.durationMinutes;
    if (plannedTotal + requiredMinutes > availableMinutes) return false;
    const orderedSlots = [...slots].sort((first, second) => first.weekday - second.weekday || first.cursor - second.cursor);
    const slot = orderedSlots.find((candidate) => candidate.end - candidate.cursor >= requiredMinutes);
    if (!slot) return false;
    appendBlock('theory', theory.durationMinutes, slot, subject);
    appendBlock('exercises', exercises.durationMinutes, slot, subject, Math.max(5, exercises.estimatedQuestions || 10));
    // Teoria e exercícios formam o mesmo ciclo; o intervalo vem somente após o ciclo completo.
    slot.cursor = Math.min(slot.end, slot.cursor + slot.breakMinutes);
    return true;
  };

  enabledActivities.filter((activity) => ['simulation', 'writing'].includes(activity.kind)).forEach((activity) => {
    for (let count = 0; count < Math.max(1, activity.weeklyFrequency); count += 1) {
      if (!placeBlock(activity.kind, activity.durationMinutes, activity.preferredWeekday, activity.estimatedQuestions || 0, undefined, false, activity.kind === 'simulation')) {
        warnings.push({ code: activity.kind, message: `Não houve espaço suficiente para ${activityLabels[activity.kind].toLowerCase()}. Ajuste a disponibilidade ou a duração.` });
      }
    }
  });

  const reviewActivity = enabledActivities.find((activity) => activity.kind === 'review');
  const theoryActivity = enabledActivities.find((activity) => activity.kind === 'theory');
  const exercisesActivity = enabledActivities.find((activity) => activity.kind === 'exercises');
  const reviewDays = [...new Set(slots.map((slot) => slot.weekday))];
  if (reviewActivity) {
    // A revisao pertence a uma disciplina. Alternamos a ordem a cada dia para
    // distribuir a retomada dos conteudos sem concentrar a semana numa materia.
    const reviewSubjects = [...selectedSubjects].sort((first, second) => Number(second.adjustedPercent) - Number(first.adjustedPercent));
    let reviewCursor = reviewSubjects.length ? Math.abs(variation) % reviewSubjects.length : 0;
    reviewDays.forEach((weekday) => {
      const reviewSubject = reviewSubjects[reviewCursor++ % reviewSubjects.length];
      if (!reviewSubject || !placeBlock('review', reviewActivity.durationMinutes, weekday, 0, reviewSubject, true)) {
        warnings.push({ code: 'review', message: `Não houve espaço suficiente para a Memorização/Revisão de ${reviewActivity.durationMinutes} minutos em ${['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'][weekday]}.` });
      }
    });
  }

  let guard = 0;
  while ((theoryActivity || exercisesActivity) && guard < 500) {
    guard += 1;
    const cycleSubject = chooseSubject('');
    if (!cycleSubject) break;
    const placedInRound = theoryActivity && exercisesActivity
      ? placeTheoryWithExercises(theoryActivity, exercisesActivity, cycleSubject)
      : theoryActivity
        ? placeBlock('theory', theoryActivity.durationMinutes, undefined, 0, cycleSubject)
        : placeBlock('exercises', exercisesActivity!.durationMinutes, undefined, Math.max(5, exercisesActivity!.estimatedQuestions || 10), cycleSubject);
    if (!placedInRound) break;
  }

  blocks.sort((first, second) => first.scheduledDate.localeCompare(second.scheduledDate) || first.startTime.localeCompare(second.startTime));
  const plannedMinutes = blocks.reduce((sum, block) => sum + block.durationMinutes, 0);
  return { weekNumber, startDate: weekStart, endDate: weekEnd, generationKey, blocks, warnings, availableMinutes, plannedMinutes, cycles: blocks.filter((block) => block.activity === 'theory').length };
}

export function projectPlanWeeks(config: PlanConfiguration): PlanWeekProjection[] {
  const weeklyMinutes = Math.min(availabilityMinutes(config.availability), config.manualWeeklyMinutes || Number.MAX_SAFE_INTEGER);
  const priorities = config.subjects.filter((subject) => subject.selected).sort((a, b) => b.percent - a.percent).slice(0, 3).map((subject) => subject.name);
  const baseStart = startOfSundayWeek(config.startDate);
  return Array.from({ length: Math.max(1, config.weeks) }, (_, index) => ({
    weekNumber: index + 1,
    startDate: addLocalDays(baseStart, index * 7),
    endDate: addLocalDays(baseStart, index * 7 + 6),
    expectedMinutes: weeklyMinutes,
    prioritySubjects: priorities,
    status: index + 1 < config.currentWeek ? 'completed' : index + 1 === config.currentWeek ? 'ready' : 'forecast',
    completion: 0
  }));
}
